import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth-context";
import Logo from "../../components/Logo";
import NotificationBell from "../../components/NotificationBell";
import { LayoutDashboard, Users, Building2, CreditCard, Cpu, ArrowLeftRight, Wallet, FileText, ShieldAlert, Settings, ScrollText, LogOut, Search, Mail, Shield, UserCheck } from "lucide-react";
import { Input } from "../../components/ui/input";

const NAV = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/businesses", label: "Businesses", icon: Building2 },
    { to: "/admin/loans", label: "Loans", icon: CreditCard },
    { to: "/admin/credit-engine", label: "Credit Engine", icon: Cpu },
    { to: "/admin/transactions", label: "Transactions", icon: ArrowLeftRight },
    { to: "/admin/collections", label: "Collections", icon: Wallet },
    { to: "/admin/insurance", label: "Insurance", icon: Shield },
    { to: "/admin/kyc", label: "KYC Reviews", icon: UserCheck },
    { to: "/admin/reports", label: "Reports", icon: FileText },
    { to: "/admin/risk", label: "Risk & Fraud", icon: ShieldAlert },
    { to: "/admin/settings", label: "Settings", icon: Settings },
    { to: "/admin/system-logs", label: "System Logs", icon: ScrollText },
];

export default function AdminShell() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    return (
        <div className="flex h-screen w-full bg-slate-50 overflow-hidden" data-testid="admin-shell">
            {/* Sidebar */}
            <aside className="w-64 gradient-admin-sidebar text-white flex-shrink-0 flex flex-col" data-testid="admin-sidebar">
                <div className="px-5 pt-5 pb-4 border-b border-white/10">
                    <Logo size={36} withText variant="dark" />
                </div>
                <nav className="flex-1 overflow-y-auto admin-scroll px-3 py-3 space-y-0.5">
                    {NAV.map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.end}
                            data-testid={`admin-nav-${item.label.replace(/\s|&/g, "")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition ${
                                    isActive ? "bg-[#0066FF] text-white shadow-lg shadow-[#0066FF]/25" : "text-slate-300 hover:bg-white/5"
                                }`
                            }
                        >
                            <item.icon size={18} />
                            <span>{item.label}</span>
                        </NavLink>
                    ))}
                </nav>
                <button
                    onClick={() => { logout(); nav("/login"); }}
                    data-testid="admin-logout-btn"
                    className="mx-3 mb-3 flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-300 hover:bg-rose-500/10 hover:text-rose-400 transition"
                >
                    <LogOut size={18} /> Logout
                </button>
            </aside>

            {/* Content */}
            <main className="flex-1 flex flex-col overflow-hidden">
                <header className="h-16 px-6 bg-white border-b border-slate-200 flex items-center justify-between flex-shrink-0">
                    <div className="relative w-80 max-w-full">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input placeholder="Search users, loans, businesses…" className="pl-9 h-10 rounded-xl bg-slate-50 border-slate-200" data-testid="admin-search" />
                    </div>
                    <div className="flex items-center gap-2">
                        <button className="size-10 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600" data-testid="admin-mail-btn"><Mail size={18} /></button>
                        <NotificationBell testid="admin-bell-btn" />
                        <div className="ml-2 flex items-center gap-2 pl-3 border-l border-slate-200">
                            {user?.avatar_url && <img src={user.avatar_url} alt="" className="size-8 rounded-full object-cover" />}
                            <div className="text-right">
                                <div className="text-sm font-semibold text-[#0A1930] leading-tight">{user?.full_name}</div>
                                <div className="text-[11px] text-slate-500">Super Admin</div>
                            </div>
                        </div>
                    </div>
                </header>
                <div className="flex-1 overflow-y-auto admin-scroll p-6">
                    <Outlet />
                </div>
                <div className="border-t border-slate-200 px-6 py-3 text-xs text-slate-400 text-center bg-white">
                    © 2026 OnePassCredit. All rights reserved.
                </div>
            </main>
        </div>
    );
}
